import { Landmark, Receipt, HandCoins, Percent, ArrowRight } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import { scrollToId } from "@/lib/scroll";

const AIDS = [
  {
    icon: Landmark,
    name: "MaPrimeRénov'",
    amount: "jusqu'à 5 000 €",
    details: [
      "Très modestes : 5 000 €",
      "Modestes : 4 000 €",
      "Intermédiaires : 3 000 €",
      "Pour une PAC air/eau posée dans un logement de plus de 15 ans, par un artisan RGE.",
    ],
    testid: "aide-card-maprimerenov",
  },
  {
    icon: Receipt,
    name: "Prime CEE",
    amount: "jusqu'à 5 800 €",
    details: [
      "Coup de pouce chauffage majoré au remplacement d'une chaudière fioul ou gaz.",
      "Montant variable selon vos revenus et votre zone climatique.",
    ],
    testid: "aide-card-cee",
  },
  {
    icon: HandCoins,
    name: "Éco-PTZ",
    amount: "jusqu'à 15 000 €",
    details: [
      "Le prêt à taux zéro finance votre reste à charge sans intérêts.",
      "Remboursable jusqu'à 20 ans. Jusqu'à 50 000 € en rénovation d'ampleur.",
    ],
    testid: "aide-card-ecoptz",
  },
  {
    icon: Percent,
    name: "TVA 5,5 %",
    amount: "sur toute la facture",
    details: [
      "Taux réduit appliqué directement sur la fourniture et la pose par un artisan RGE.",
      "Pour tout logement de plus de 2 ans.",
    ],
    testid: "aide-card-tva",
  },
];

export default function Aides() {
  return (
    <section
      id="aides"
      data-testid="section-aides"
      className="relative bg-paper py-24 text-ink sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <FadeIn>
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-700 sm:text-xs">
            Les aides 2026
          </p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-5xl">
            Un projet très largement financé.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
            Remplacer une chaudière gaz ou fioul par une pompe à chaleur
            air/eau est l'un des travaux les plus aidés de France. Cumulez
            MaPrimeRénov', la prime CEE, l'éco-PTZ et la TVA à 5,5 % — et
            laissez-nous gérer les dossiers.
          </p>
        </FadeIn>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {AIDS.map((a, i) => (
            <FadeIn key={a.name} delay={i * 0.08} className="h-full">
              <div
                data-testid={a.testid}
                className="flex h-full flex-col rounded-2xl border border-emerald-900/10 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-glow"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-400/10 text-emerald-700">
                  <a.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-display text-lg font-bold">{a.name}</h3>
                <p className="mt-2 font-display text-2xl font-extrabold text-forest">
                  {a.amount}
                </p>
                <ul className="mt-5 space-y-2.5 border-t border-slate-100 pt-5">
                  {a.details.map((d, j) => (
                    <li
                      key={j}
                      className="flex gap-2 text-[13px] leading-relaxed text-slate-600"
                    >
                      <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-emerald-500" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.1}>
          <div className="mt-12 flex flex-col items-start justify-between gap-6 rounded-2xl border border-emerald-900/10 bg-forest p-8 text-white sm:flex-row sm:items-center sm:p-10">
            <p className="max-w-xl text-sm leading-relaxed text-white/75">
              <strong className="text-white">
                L'ordre des démarches est essentiel :
              </strong>{" "}
              le dossier d'aides se dépose avant la signature du devis. Barèmes
              2026 indicatifs, sous conditions de ressources — nous nous
              chargeons de tout.
            </p>
            <button
              data-testid="aides-cta-simulator"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#simulateur");
              }}
              className="group flex shrink-0 items-center gap-2 rounded-full bg-emerald-400 px-7 py-4 text-base font-bold text-forest-deep transition-all hover:scale-[1.03] hover:bg-emerald-300"
            >
              Calculer mes aides
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
